import { z } from 'zod';

/**
 * Extended step contracts for Tiers 1-12 of the PO Scenario Coverage
 * build-out. Each section maps to a tier in the build plan.
 *
 * Wire model
 * -----------
 * Step config in the editor remains a flat `Record<string, string>` —
 * structured values (repeaters, field-groups, conditional groups) are
 * JSON-encoded into a single field. The helpers in this file
 * (`parseExtendedField` etc.) round-trip them safely.
 *
 * Adding a new step
 * -----------------
 *  1. Add the Zod schema for its structured payload here.
 *  2. Add the `StepType` literal in the flow-builder registry.
 *  3. Add the runner handler in `packages/mvp/src/lib/extended-step-handlers.ts`.
 *  4. Wire the case in `packages/mvp/src/step-processor.ts`.
 */

// ============================================================
// Shared primitives
// ============================================================

export const ValueKindSchema = z.enum(['string', 'integer', 'number', 'boolean', 'date', 'currency']);
export type ValueKind = z.infer<typeof ValueKindSchema>;

export const ExtractFromSchema = z.enum(['dom', 'last-api-response', 'named-response']);
export type ExtractFrom = z.infer<typeof ExtractFromSchema>;

/**
 * Locator qualifier DSL (Tier 10A). Optional refinements applied on
 * top of the base `target` selector by the resolver.
 */
export const LocatorQualifiersSchema = z
  .object({
    nth: z.number().int().min(0).optional(),
    within: z.string().optional(),
    near: z.string().optional(),
    farFrom: z.string().optional(),
    position: z
      .enum([
        'top',
        'bottom',
        'left',
        'right',
        'top-left',
        'top-right',
        'bottom-left',
        'bottom-right',
        'visually-first',
        'visually-last',
      ])
      .optional(),
  })
  .partial();
export type LocatorQualifiers = z.infer<typeof LocatorQualifiersSchema>;

/**
 * Cell locator (Tier 9). Targets a cell in a detected data grid.
 */
export const CellColumnSchema = z.union([
  z.object({ header: z.string() }),
  z.object({ headerMatchesRegex: z.string() }),
  z.object({ index: z.number().int().nonnegative() }),
]);
export const CellRowSchema = z.union([
  z.object({ anchorText: z.string(), anchorColumn: z.string().optional() }),
  z.object({ index: z.number().int().nonnegative() }),
  z.object({ where: z.object({ column: z.string(), value: z.string() }) }),
]);
export const CellLocatorSchema = z.object({
  grid: z.string().optional(),
  column: CellColumnSchema,
  row: CellRowSchema,
});
export type CellLocator = z.infer<typeof CellLocatorSchema>;

// ============================================================
// Tier 1 — quick wins
// ============================================================

export const AssertFormulaFieldsSchema = z.object({
  expression: z.string().min(1),
  expected: z.string(),
  tolerance: z.string().optional(),
  outputVar: z.string().optional(),
});
export type AssertFormulaFields = z.infer<typeof AssertFormulaFieldsSchema>;

export const DateOrderSchema = z.enum(['before', 'after', 'same', 'before-or-equal', 'after-or-equal']);
export const AssertDateOrderFieldsSchema = z.object({
  leftSelector: z.string(),
  rightSelector: z.string(),
  order: DateOrderSchema,
  format: z.string().optional(),
  attribute: z.string().optional(),
});
export type AssertDateOrderFields = z.infer<typeof AssertDateOrderFieldsSchema>;

export const TotpGenerateFieldsSchema = z.object({
  secretRef: z.string().min(1),
  outputVar: z.string().default('totp_code'),
  digits: z.string().optional(),
  period: z.string().optional(),
  algorithm: z.enum(['SHA-1', 'SHA-256', 'SHA-512']).optional(),
});
export type TotpGenerateFields = z.infer<typeof TotpGenerateFieldsSchema>;

export const DialogActionSchema = z.enum(['accept', 'dismiss']);
export const DialogStepFieldsSchema = z.object({
  action: DialogActionSchema,
  promptText: z.string().optional(),
  scope: z.enum(['next', 'auto']).default('next'),
  /** Max wait for a dialog to appear before FAIL (honesty: install alone is not PASS). */
  timeoutMs: z.number().int().positive().default(30_000),
});
export type DialogStepFields = z.infer<typeof DialogStepFieldsSchema>;

export const FakerSpecSchema = z.union([
  z.string(),
  z.object({ method: z.string(), args: z.array(z.unknown()).optional() }),
  z.object({ template: z.string() }),
]);
export type FakerSpec = z.infer<typeof FakerSpecSchema>;

export const ExtractVarFieldsSchema = z.object({
  name: z.string().min(1),
  selector: z.string().optional(),
  attribute: z.string().optional(),
  kind: ValueKindSchema.optional(),
  from: ExtractFromSchema.optional(),
  jsonPath: z.string().optional(),
  responseVar: z.string().optional(),
  literal: z.string().optional(),
});
export type ExtractVarFields = z.infer<typeof ExtractVarFieldsSchema>;

export const DeltaWatchSchema = z.object({
  name: z.string().min(1),
  selector: z.string(),
  attribute: z.string().optional(),
  kind: ValueKindSchema.optional(),
});
export type DeltaWatch = z.infer<typeof DeltaWatchSchema>;

/**
 * Expectation for `assert-delta` and `assert-snapshot`. Encoded as a
 * string in the flat field bag with a leading sigil so the simple
 * delta forms (`+1`, `-1`) read naturally.
 *
 * Recognised shapes:
 *  - `+N` / `-N` / `0`        → arithmetic delta
 *  - `=value`                  → after === value
 *  - `>value` / `<value`       → comparison
 *  - `~formula:value(±tol)`   → formula equality with optional tolerance
 *  - `JSON({...})`            → escape hatch, full ExpectationSchema
 */
export const ExpectationSchema = z.union([
  z.object({ delta: z.number() }),
  z.object({ absolute: z.union([z.string(), z.number()]) }),
  z.object({ greaterThan: z.union([z.string(), z.number()]) }),
  z.object({ lessThan: z.union([z.string(), z.number()]) }),
  z.object({ formula: z.string(), tolerance: z.number().optional() }),
  z.object({ matches: z.string() }),
]);
export type Expectation = z.infer<typeof ExpectationSchema>;

export const AssertDeltaFieldsSchema = z.object({
  watch: z.array(DeltaWatchSchema).min(1),
  expect: z.record(z.string(), ExpectationSchema),
  waitForChange: z.boolean().default(true),
  timeoutMs: z.number().int().positive().default(5000),
  pollIntervalMs: z.number().int().positive().default(250),
});
export type AssertDeltaFields = z.infer<typeof AssertDeltaFieldsSchema>;

export const SnapshotFieldSpecSchema = z.object({
  name: z.string().min(1),
  selector: z.string(),
  attribute: z.string().optional(),
  kind: ValueKindSchema.optional(),
});
export type SnapshotFieldSpec = z.infer<typeof SnapshotFieldSpecSchema>;

export const CaptureSnapshotFieldsSchema = z.object({
  name: z.string().min(1),
  fields: z.array(SnapshotFieldSpecSchema).min(1),
  persist: z.enum(['run', 'project', 'none']).default('none'),
});
export type CaptureSnapshotFields = z.infer<typeof CaptureSnapshotFieldsSchema>;

export const AssertSnapshotFieldsSchema = z.object({
  name: z.string().min(1),
  expect: z.record(z.string(), ExpectationSchema).optional(),
});
export type AssertSnapshotFields = z.infer<typeof AssertSnapshotFieldsSchema>;

// ============================================================
// Tier 2 — data layer
// ============================================================

export const SutDatasourceKindSchema = z.enum(['postgres', 'mysql', 'sqlite']);
export const SutDatasourceConfigSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  kind: SutDatasourceKindSchema,
  host: z.string().optional(),
  port: z.number().int().optional(),
  database: z.string(),
  user: z.string().optional(),
  passwordSecretRef: z.string().optional(),
  ssl: z.boolean().optional(),
});
export type SutDatasourceConfig = z.infer<typeof SutDatasourceConfigSchema>;

export const DbQueryFieldsSchema = z.object({
  datasourceId: z.string().min(1),
  query: z.string().min(1),
  params: z.string().optional(),
  outputVar: z.string().optional(),
});
export type DbQueryFields = z.infer<typeof DbQueryFieldsSchema>;

export const UploadFileFieldsSchema = z.object({
  selector: z.string(),
  filePath: z.string().optional(),
  fileRef: z.string().optional(),
});
export type UploadFileFields = z.infer<typeof UploadFileFieldsSchema>;

export const DownloadFileFieldsSchema = z.object({
  outputVar: z.string().default('download'),
  strategy: z.enum(['ui-click', 'http']).default('ui-click'),
  /**
   * B1 — optional UI trigger for `ui-click`. When set, the handler arms
   * `waitForEvent('download')` then clicks this locator (atomic). When
   * omitted, behaviour is unchanged: wait for a download started by a
   * prior step. Same field name as upload-file's locator key.
   */
  selector: z.string().optional(),
  url: z.string().optional(),
  method: z.string().optional(),
  headers: z.string().optional(),
  body: z.string().optional(),
  timeoutMs: z.number().int().positive().default(30000),
});
export type DownloadFileFields = z.infer<typeof DownloadFileFieldsSchema>;

export const StorageSeedFieldsSchema = z.object({
  cookies: z.string().optional(),
  localStorage: z.string().optional(),
  sessionStorage: z.string().optional(),
  origin: z.string().optional(),
});
export type StorageSeedFields = z.infer<typeof StorageSeedFieldsSchema>;

// ============================================================
// Tier 3 — multi-context
// ============================================================

export const SwitchTabFieldsSchema = z.object({
  strategy: z.enum(['by-index', 'by-url-pattern', 'latest']).default('latest'),
  index: z.number().int().nonnegative().optional(), // required when strategy=by-index (resolved in dispatch)
  urlPattern: z.string().optional(),
});
export type SwitchTabFields = z.infer<typeof SwitchTabFieldsSchema>;

export const FrameSwitchFieldsSchema = z.object({
  selector: z.string().optional(),
  nameMatches: z.string().optional(),
  urlMatches: z.string().optional(),
});
export type FrameSwitchFields = z.infer<typeof FrameSwitchFieldsSchema>;

export const PiercedClickFieldsSchema = z.object({
  shadowPath: z.string().min(1),
  innerSelector: z.string().min(1),
});
export type PiercedClickFields = z.infer<typeof PiercedClickFieldsSchema>;

// ============================================================
// Tier 4 — verification depth
// ============================================================

export const MockApiFieldsSchema = z.object({
  urlPattern: z.string().min(1),
  method: z.string().optional(),
  status: z.number().int().min(100).max(599).default(200),
  body: z.string().optional(),
  headers: z.string().optional(),
  delayMs: z.number().int().nonnegative().optional(),
  scope: z.enum(['until-cleared', 'next-only']).default('until-cleared'),
});
export type MockApiFields = z.infer<typeof MockApiFieldsSchema>;

export const RecordHarFieldsSchema = z.object({
  action: z.enum(['start', 'stop']).default('start'),
  outputVar: z.string().optional(),
});
export type RecordHarFields = z.infer<typeof RecordHarFieldsSchema>;

export const PerfCheckFieldsSchema = z.object({
  metric: z.enum(['LCP', 'CLS', 'INP', 'TTFB', 'FCP', 'load']),
  lessThan: z.string().optional(),
  greaterThan: z.string().optional(),
  outputVar: z.string().optional(),
  /** Performance budgets (ms except CLS unitless). Absent = not asserted. */
  maxLoadTime: z.string().optional(),
  maxFCP: z.string().optional(),
  maxLCP: z.string().optional(),
  maxCLS: z.string().optional(),
  maxTBT: z.string().optional(),
});
export type PerfCheckFields = z.infer<typeof PerfCheckFieldsSchema>;

export const A11yCheckFieldsSchema = z.object({
  selector: z.string().optional(),
  rules: z.string().optional(),
  failOnSeverity: z.enum(['minor', 'moderate', 'serious', 'critical']).default('serious'),
  outputVar: z.string().optional(),
  /** Palette WCAG level (A/AA/AAA). Maps to axe runOnly tags unless explicit tags override. */
  level: z.string().optional(),
  maxViolations: z.string().optional(),
  tags: z.string().optional(),
});
export type A11yCheckFields = z.infer<typeof A11yCheckFieldsSchema>;

/**
 * `accessibility-contrast` — Phase A runner-side WCAG contrast
 * scan. Walks the DOM in-page, computes FG (computed-style
 * colour) and BG (ancestor walk for the first opaque background)
 * for every text-bearing element, and runs `assessContrast`
 * from contracts.
 *
 * `states` is an opt-in cross-state extension — Phase B can
 * iterate over `hover`, `focus`, `active` by toggling CSS
 * pseudo-state overrides in-page. v1 default is just `resting`.
 *
 * `paletteSnap` is the gate for the Phase C brand-palette
 * constraint. When `on` (the default), the API-side
 * worker-write route will snap suggested fixes to the org's
 * default palette.
 */
export const ContrastCheckFieldsSchema = z.object({
  selector: z.string().default('body'),
  level: z.enum(['AA', 'AAA']).default('AA'),
  // Comma-separated string from the no-code editor — the
  // runner splits/validates against the four allowed values.
  states: z.string().default('resting'),
  paletteSnap: z.enum(['on', 'off']).default('on'),
  maxViolations: z.coerce.number().int().min(0).default(0),
  outputVar: z.string().optional(),
});
export type ContrastCheckFields = z.infer<typeof ContrastCheckFieldsSchema>;

// ============================================================
// Tier 5 — out-of-band channels
// ============================================================

export const MailboxKindSchema = z.enum(['mailosaur', 'mailtrap', 'mailhog', 'microsoft365', 'gmail', 'imap']);
export type MailboxKind = z.infer<typeof MailboxKindSchema>;

export const MailboxConfigSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  kind: MailboxKindSchema,
  config: z.record(z.string(), z.unknown()),
  vaultSecretRef: z.string().optional(),
  createdAt: z.string().optional(),
});
export type MailboxConfig = z.infer<typeof MailboxConfigSchema>;

export const EmailFilterSchema = z.object({
  from: z.string().optional(),
  subjectContains: z.string().optional(),
  withinSeconds: z.number().int().positive().optional(),
  unreadOnly: z.boolean().optional(),
  bodyMatch: z
    .object({
      regex: z.string(),
      group: z.union([z.string(), z.number()]).optional(),
    })
    .optional(),
});
export type EmailFilter = z.infer<typeof EmailFilterSchema>;

export const EmailExtractSchema = z.union([
  z.object({ mode: z.literal('default-otp-regex') }),
  z.object({
    mode: z.literal('regex'),
    pattern: z.string(),
    group: z.union([z.string(), z.number()]).optional(),
  }),
  z.object({ mode: z.literal('structured') }),
]);
export type EmailExtract = z.infer<typeof EmailExtractSchema>;

export const EmailOtpFieldsSchema = z.object({
  mailboxId: z.string().min(1),
  filter: EmailFilterSchema,
  extract: EmailExtractSchema.default({ mode: 'default-otp-regex' }),
  outputVar: z.string().default('otp'),
  cleanup: z.enum(['mark-read', 'delete', 'none']).default('mark-read'),
  timeoutMs: z.number().int().positive().default(60000),
  pollIntervalMs: z.number().int().positive().default(2000),
});
export type EmailOtpFields = z.infer<typeof EmailOtpFieldsSchema>;

export const EmailAssertionSchema = z.union([
  z.object({ kind: z.literal('subjectEquals'), value: z.string() }),
  z.object({ kind: z.literal('subjectContains'), value: z.string() }),
  z.object({ kind: z.literal('bodyContains'), value: z.string() }),
  z.object({ kind: z.literal('bodyMatchesRegex'), value: z.string() }),
  z.object({ kind: z.literal('hasAttachment'), value: z.string().optional() }),
  z.object({ kind: z.literal('headerEquals'), header: z.string(), value: z.string() }),
]);
export type EmailAssertion = z.infer<typeof EmailAssertionSchema>;

export const EmailAssertFieldsSchema = z.object({
  mailboxId: z.string().min(1),
  filter: EmailFilterSchema,
  assertions: z.array(EmailAssertionSchema).min(1),
  timeoutMs: z.number().int().positive().default(60000),
});
export type EmailAssertFields = z.infer<typeof EmailAssertFieldsSchema>;

export const SmsProviderSchema = z.enum(['twilio', 'vonage', 'messagebird', 'mock']);
export const SmsOtpFieldsSchema = z.object({
  provider: SmsProviderSchema,
  phoneNumberRef: z.string().min(1),
  pattern: z.string().optional(),
  outputVar: z.string().default('otp'),
  timeoutMs: z.number().int().positive().default(60000),
});
export type SmsOtpFields = z.infer<typeof SmsOtpFieldsSchema>;

export const WsAssertFieldsSchema = z.object({
  url: z.string().min(1),
  headers: z.string().optional(),
  match: z
    .object({
      jsonPath: z.string().optional(),
      contains: z.string().optional(),
      regex: z.string().optional(),
    })
    .partial(),
  timeoutMs: z.number().int().positive().default(15000),
});
export type WsAssertFields = z.infer<typeof WsAssertFieldsSchema>;

export const SseAssertFieldsSchema = WsAssertFieldsSchema.extend({
  eventName: z.string().optional(),
});
export type SseAssertFields = z.infer<typeof SseAssertFieldsSchema>;

// ============================================================
// Tier 6 — downloaded-artefact validation
// ============================================================

export const FileAssertionSchema = z.union([
  z.object({ kind: z.literal('filenameEquals'), value: z.string() }),
  z.object({ kind: z.literal('filenameMatches'), value: z.string() }),
  z.object({ kind: z.literal('sizeEquals'), value: z.number() }),
  z.object({
    kind: z.literal('sizeBetween'),
    value: z.tuple([z.number(), z.number()]),
  }),
  z.object({ kind: z.literal('mimeTypeEquals'), value: z.string() }),
  z.object({ kind: z.literal('magicBytesEquals'), value: z.string() }),
  z.object({ kind: z.literal('sha256Equals'), value: z.string() }),
  z.object({ kind: z.literal('existsAndReadable') }),
]);
export type FileAssertion = z.infer<typeof FileAssertionSchema>;

export const AssertFileFieldsSchema = z.object({
  fileVar: z.string().default('download'),
  assertions: z.array(FileAssertionSchema).min(1),
});
export type AssertFileFields = z.infer<typeof AssertFileFieldsSchema>;

export const FileContentKindSchema = z.enum(['text', 'json', 'csv', 'xlsx', 'pdf', 'zip', 'image', 'binary']);
export type FileContentKind = z.infer<typeof FileContentKindSchema>;

export const AssertFileContentFieldsSchema = z.object({
  fileVar: z.string().default('download'),
  kind: FileContentKindSchema,
  /** Free-form structured assertion bag, parsed per-kind by the runner. */
  spec: z.record(z.string(), z.unknown()),
});
export type AssertFileContentFields = z.infer<typeof AssertFileContentFieldsSchema>;

export const CompareModeSchema = z.enum([
  'binary',
  'text-normalised',
  'csv-rows',
  'xlsx-cells',
  'pdf-text',
  'json-deep',
]);
export const CompareFilesFieldsSchema = z.object({
  fileVarA: z.string(),
  fileVarB: z.string(),
  mode: CompareModeSchema,
  tolerance: z.number().optional(),
  ignoreOrder: z.boolean().optional(),
});
export type CompareFilesFields = z.infer<typeof CompareFilesFieldsSchema>;

export const CompareFileToUiFieldsSchema = z.object({
  fileVar: z.string().default('download'),
  tableSelector: z.string(),
  columnMap: z.record(z.string(), z.string()),
  ignoreOrder: z.boolean().optional(),
  ignoreColumns: z.array(z.string()).optional(),
  tolerance: z.number().optional(),
  caseInsensitive: z.boolean().optional(),
});
export type CompareFileToUiFields = z.infer<typeof CompareFileToUiFieldsSchema>;

export const CompareFileToSqlFieldsSchema = z.object({
  fileVar: z.string().default('download'),
  datasourceId: z.string(),
  query: z.string(),
  params: z.string().optional(),
  columnMap: z.record(z.string(), z.string()).optional(),
  ignoreOrder: z.boolean().optional(),
  tolerance: z.number().optional(),
  caseInsensitive: z.boolean().optional(),
});
export type CompareFileToSqlFields = z.infer<typeof CompareFileToSqlFieldsSchema>;

export const CompareFileToApiFieldsSchema = z.object({
  fileVar: z.string().default('download'),
  responseVar: z.string().optional(),
  rowsJsonPath: z.string(),
  columnMap: z.record(z.string(), z.string()).optional(),
  ignoreOrder: z.boolean().optional(),
  tolerance: z.number().optional(),
  caseInsensitive: z.boolean().optional(),
});
export type CompareFileToApiFields = z.infer<typeof CompareFileToApiFieldsSchema>;

export const OcrFieldsSchema = z.object({
  fileVar: z.string().default('download'),
  language: z.string().default('eng'),
  contains: z.array(z.string()).optional(),
  matchesRegex: z.string().optional(),
  wordCountAtLeast: z.number().int().nonnegative().optional(),
});
export type OcrFields = z.infer<typeof OcrFieldsSchema>;

// ============================================================
// Tier 7 — reliability + ops
// ============================================================

export const NetworkConditionsFieldsSchema = z.object({
  preset: z.enum(['offline', 'slow-3g', 'fast-3g', '4g', 'wifi', 'reset']).default('reset'),
  downloadKbps: z.number().optional(),
  uploadKbps: z.number().optional(),
  latencyMs: z.number().optional(),
});
export type NetworkConditionsFields = z.infer<typeof NetworkConditionsFieldsSchema>;

export const ClockControlFieldsSchema = z.object({
  action: z.enum(['install', 'set', 'fast-forward', 'uninstall']),
  isoTime: z.string().optional(),
  ms: z.number().int().nonnegative().optional(),
});
export type ClockControlFields = z.infer<typeof ClockControlFieldsSchema>;

// ============================================================
// Tier 8 — Okta push
// ============================================================

export const WaitOktaPushFieldsSchema = z.object({
  oktaDomain: z.string().min(1),
  apiTokenRef: z.string().min(1),
  userId: z.string().min(1),
  factorId: z.string().optional(),
  autoApprove: z.boolean().default(false),
  timeoutMs: z.number().int().positive().default(60000),
  pollIntervalMs: z.number().int().positive().default(2000),
});
export type WaitOktaPushFields = z.infer<typeof WaitOktaPushFieldsSchema>;

// ============================================================
// Tier 9 — grid-aware locators
// ============================================================

export const SetCellFieldsSchema = z.object({
  locator: CellLocatorSchema,
  value: z.string(),
  inputType: z.enum(['text', 'select', 'checkbox', 'radio', 'datepicker', 'unit-input', 'auto']).default('auto'),
});
export type SetCellFields = z.infer<typeof SetCellFieldsSchema>;

export const AssertCellFieldsSchema = z.object({
  locator: CellLocatorSchema,
  expect: ExpectationSchema,
  kind: ValueKindSchema.optional(),
});
export type AssertCellFields = z.infer<typeof AssertCellFieldsSchema>;

export const ReadCellFieldsSchema = z.object({
  locator: CellLocatorSchema,
  outputVar: z.string().min(1),
  kind: ValueKindSchema.optional(),
});
export type ReadCellFields = z.infer<typeof ReadCellFieldsSchema>;

export const WithRowFieldsSchema = z.object({
  grid: z.string().optional(),
  anchor: z.union([
    z.object({ text: z.string() }),
    z.object({ column: z.string(), text: z.string() }),
    z.object({ index: z.number().int().nonnegative() }),
  ]),
});
export type WithRowFields = z.infer<typeof WithRowFieldsSchema>;

// ============================================================
// Tier 10A — locator qualifiers + waits
// ============================================================

export const WithinSectionFieldsSchema = z.object({
  selector: z.string().optional(),
  sectionText: z.string().optional(),
  role: z.string().optional(),
});
export type WithinSectionFields = z.infer<typeof WithinSectionFieldsSchema>;

export const WaitUntilKindSchema = z.enum([
  'visible',
  'hidden',
  'enabled',
  'disabled',
  'text-equals',
  'text-contains',
  'count-equals',
  'stable',
]);

export const WaitUntilFieldsSchema = z.object({
  kind: WaitUntilKindSchema,
  selector: z.string().optional(),
  expectedText: z.string().optional(),
  expectedCount: z.number().int().nonnegative().optional(),
  timeoutMs: z.number().int().positive().default(10000),
});
export type WaitUntilFields = z.infer<typeof WaitUntilFieldsSchema>;

// ============================================================
// Tier 10B — rich UI components
// ============================================================

export const RichInputFieldsSchema = z.object({
  selector: z.string(),
  value: z.string(),
  mode: z.enum(['replace', 'append']).default('replace'),
  framework: z
    .enum(['auto', 'slate', 'quill', 'tiptap', 'prosemirror', 'draft', 'monaco', 'codemirror'])
    .default('auto'),
});
export type RichInputFields = z.infer<typeof RichInputFieldsSchema>;

export const PickDateFieldsSchema = z.object({
  selector: z.string(),
  date: z.string(),
  format: z.string().optional(),
  library: z.enum(['auto', 'mui', 'antd', 'react-datepicker', 'flatpickr']).default('auto'),
});
export type PickDateFields = z.infer<typeof PickDateFieldsSchema>;

export const SetSliderFieldsSchema = z.object({
  selector: z.string(),
  value: z.string(),
  min: z.string().optional(),
  max: z.string().optional(),
});
export type SetSliderFields = z.infer<typeof SetSliderFieldsSchema>;

export const ListItemFieldsSchema = z.object({
  selector: z.string(),
  values: z.array(z.string()).min(1),
});
export type ListItemFields = z.infer<typeof ListItemFieldsSchema>;

export const ToastAssertFieldsSchema = z.object({
  textContains: z.string().optional(),
  textMatchesRegex: z.string().optional(),
  timeoutMs: z.number().int().positive().default(5000),
  toastSelector: z.string().optional(),
});
export type ToastAssertFields = z.infer<typeof ToastAssertFieldsSchema>;

// ============================================================
// Tier 10C — keyboard, charts, locator hints
// ============================================================

export const PressShortcutFieldsSchema = z.object({
  combo: z.string().min(1),
  selector: z.string().optional(),
});
export type PressShortcutFields = z.infer<typeof PressShortcutFieldsSchema>;

export const TabThroughFieldsSchema = z.object({
  count: z.number().int().positive().default(1),
  outputVar: z.string().optional(),
});
export type TabThroughFields = z.infer<typeof TabThroughFieldsSchema>;

export const ChartClickFieldsSchema = z.object({
  chartSelector: z.string(),
  series: z.string().optional(),
  pointLabel: z.string().optional(),
  pointIndex: z.number().int().nonnegative().optional(),
  library: z.enum(['auto', 'chartjs', 'recharts', 'd3', 'highcharts']).default('auto'),
});
export type ChartClickFields = z.infer<typeof ChartClickFieldsSchema>;

// ============================================================
// Tier 11 — data-driven
// ============================================================

export const LoadDataSourceSchema = z.union([
  z.object({ fileRef: z.string(), version: z.union([z.string(), z.number()]).optional() }),
  z.object({ inlineRows: z.string() }),
  z.object({ url: z.string(), headers: z.string().optional() }),
  z.object({ secretBlobRef: z.string() }),
  z.object({ filePath: z.string() }),
]);

export const LoadDataFieldsSchema = z.object({
  source: LoadDataSourceSchema,
  format: z.enum(['csv', 'tsv', 'json', 'xlsx', 'yaml', 'auto']).default('auto'),
  delimiter: z.string().optional(),
  sheet: z.string().optional(),
  /** When false, Excel/CSV-style index keys; default true = first row headers. */
  hasHeader: z.boolean().optional(),
  encoding: z.string().optional(),
  select: z.array(z.string()).optional(),
  filter: z.string().optional(),
  transform: z.record(z.string(), z.string()).optional(),
  sort: z.string().optional(),
  limit: z.number().int().positive().optional(),
  skip: z.number().int().nonnegative().optional(),
  sample: z.number().int().positive().optional(),
  seed: z.number().int().optional(),
  validateSchema: z
    .object({
      columns: z.array(
        z.object({
          name: z.string(),
          kind: ValueKindSchema.optional(),
          required: z.boolean().optional(),
        }),
      ),
    })
    .optional(),
  outputVar: z.string().default('dataset'),
});
export type LoadDataFields = z.infer<typeof LoadDataFieldsSchema>;

export const LoadDataFakerFieldsSchema = z.object({
  schema: z.record(z.string(), FakerSpecSchema),
  count: z.number().int().positive(),
  seed: z.number().int().optional(),
  outputVar: z.string().default('dataset'),
});
export type LoadDataFakerFields = z.infer<typeof LoadDataFakerFieldsSchema>;

export const LoadDataPairwiseFieldsSchema = z.object({
  parameters: z
    .array(
      z.object({
        name: z.string(),
        values: z.array(z.unknown()).min(1),
      }),
    )
    .min(2),
  outputVar: z.string().default('dataset'),
});
export type LoadDataPairwiseFields = z.infer<typeof LoadDataPairwiseFieldsSchema>;

export const BoundarySpecSchema = z.object({
  name: z.string(),
  kind: z.enum(['integer', 'number', 'string', 'date']),
  min: z.union([z.string(), z.number()]).optional(),
  max: z.union([z.string(), z.number()]).optional(),
  length: z.number().int().positive().optional(),
  includeInvalid: z.boolean().optional(),
  includeOffByOne: z.boolean().optional(),
  includeFuzz: z.enum(['sql', 'xss', 'unicode', 'long']).optional(),
});
export type BoundarySpec = z.infer<typeof BoundarySpecSchema>;

export const LoadDataBoundaryFieldsSchema = z.object({
  columns: z.array(BoundarySpecSchema).min(1),
  outputVar: z.string().default('dataset'),
});
export type LoadDataBoundaryFields = z.infer<typeof LoadDataBoundaryFieldsSchema>;

export const FillFormFromRowFieldsSchema = z.object({
  sourceRow: z.string().optional(),
  fieldMap: z
    .array(
      z.object({
        column: z.string(),
        target: z.union([z.string(), CellLocatorSchema]),
        kind: z.string().optional(),
        transform: z.string().optional(),
      }),
    )
    .min(1),
  skipMissing: z.boolean().default(false),
  failFast: z.boolean().default(false),
});
export type FillFormFromRowFields = z.infer<typeof FillFormFromRowFieldsSchema>;

// ============================================================
// Tier 12 — network monitoring
// ============================================================

export const NetworkEntrySchema = z.object({
  id: z.string(),
  url: z.string(),
  method: z.string(),
  status: z.number().optional(),
  durationMs: z.number().optional(),
  startTime: z.number(),
  endTime: z.number().optional(),
  failureText: z.string().optional(),
  requestHeaders: z.record(z.string(), z.string()).optional(),
  responseHeaders: z.record(z.string(), z.string()).optional(),
  requestBody: z.string().optional(),
  responseBody: z.string().optional(),
});
export type NetworkEntry = z.infer<typeof NetworkEntrySchema>;

export const WaitForNetworkIdleFieldsSchema = z.object({
  idleMs: z.number().int().nonnegative().default(500),
  timeoutMs: z.number().int().positive().default(30000),
  ignoreHosts: z.array(z.string()).optional(),
});
export type WaitForNetworkIdleFields = z.infer<typeof WaitForNetworkIdleFieldsSchema>;

export const WaitForRequestFieldsSchema = z.object({
  urlPattern: z.string().min(1),
  method: z.string().optional(),
  timeoutMs: z.number().int().positive().default(15000),
  outputVar: z.string().optional(),
});
export type WaitForRequestFields = z.infer<typeof WaitForRequestFieldsSchema>;

export const WaitForResponseFieldsSchema = WaitForRequestFieldsSchema.extend({
  statusCode: z.union([z.number().int(), z.string()]).optional(),
});
export type WaitForResponseFields = z.infer<typeof WaitForResponseFieldsSchema>;

export const ApiCoverageEntrySchema = z.object({
  urlPattern: z.string(),
  method: z.string().optional(),
  status: z.union([z.number().int(), z.string()]).optional(),
  requireSuccess: z.boolean().optional(),
  atLeast: z.number().int().nonnegative().optional(),
  atMost: z.number().int().nonnegative().optional(),
});
export type ApiCoverageEntry = z.infer<typeof ApiCoverageEntrySchema>;

export const AssertApiCoverageFieldsSchema = z.object({
  expected: z.array(ApiCoverageEntrySchema).min(1),
  mode: z.enum(['subset', 'exact']).default('subset'),
  since: z.enum(['last-navigation', 'last-step']).default('last-navigation'),
  ignoreHosts: z.array(z.string()).optional(),
});
export type AssertApiCoverageFields = z.infer<typeof AssertApiCoverageFieldsSchema>;

export const AssertNoFailedRequestsFieldsSchema = z.object({
  since: z.enum(['last-navigation', 'last-step']).default('last-navigation'),
  failOn: z.enum(['4xx', '5xx', 'any-error']).default('any-error'),
  ignoreHosts: z.array(z.string()).optional(),
  excludeStatuses: z.array(z.number().int()).optional(),
  excludeUrlPatterns: z.array(z.string()).optional(),
});
export type AssertNoFailedRequestsFields = z.infer<typeof AssertNoFailedRequestsFieldsSchema>;

export const AssertNetworkKindSchema = z.enum([
  'request-made',
  'no-request',
  'order',
  'request-payload',
  'response-payload',
  'request-header',
  'response-time',
  'count',
]);

export const AssertNetworkFieldsSchema = z.object({
  kind: AssertNetworkKindSchema,
  urlPattern: z.string().optional(),
  method: z.string().optional(),
  patterns: z.array(z.string()).optional(),
  jsonPath: z.string().optional(),
  op: z.enum(['eq', 'neq', 'contains', 'matches']).optional(),
  value: z.union([z.string(), z.number()]).optional(),
  header: z.string().optional(),
  lessThanMs: z.number().int().positive().optional(),
  atLeast: z.number().int().nonnegative().optional(),
  atMost: z.number().int().nonnegative().optional(),
  exactly: z.number().int().nonnegative().optional(),
  since: z.enum(['last-navigation', 'last-step']).default('last-navigation'),
});
export type AssertNetworkFields = z.infer<typeof AssertNetworkFieldsSchema>;

// ============================================================
// Tier 13 — real-world gap closers (surfaced by the Amazon walkthrough)
//
// These are not yet a tier in the master plan; they are the smallest
// useful answers to the most common "Rynora doesn't have a step for
// this" complaints from real e-commerce + SaaS journeys.
// ============================================================

/**
 * `pick-suggestion` — type a query into a search/autocomplete input,
 * wait for the suggestion list to open, then click one of the
 * suggestions. Replaces the manual `type` + `wait-until-visible` +
 * `click` triple every flow author writes today.
 */
export const PickSuggestionFieldsSchema = z.object({
  inputTarget: z.string().min(1),
  query: z.string().min(1),
  /** CSS or role selector for the suggestion list container. */
  listTarget: z.string().optional(),
  /**
   * How to choose which suggestion to click. `text` is the most
   * common; `index` is the fallback for ranked lists.
   */
  pick: z.union([
    z.object({ text: z.string() }),
    z.object({ contains: z.string() }),
    z.object({ index: z.number().int().nonnegative() }),
  ]),
  /** Items inside the list; defaults to `[role="option"]`. */
  optionTarget: z.string().default('[role="option"]'),
  timeoutMs: z.number().int().positive().default(5000),
  outputVar: z.string().optional(),
});
export type PickSuggestionFields = z.infer<typeof PickSuggestionFieldsSchema>;

/**
 * `dismiss-overlay` — auto-detect a cookie / consent / promotional
 * overlay at run-start (or on demand) and click its dismiss control.
 * The runner ships a small library of well-known dismiss patterns
 * (data-testid hints, button text matchers) and lets the author
 * extend it inline.
 */
export const DismissOverlayFieldsSchema = z.object({
  /** Optional CSS selector that matches the overlay container. */
  overlayTarget: z.string().optional(),
  /**
   * Friendly text the dismiss control likely shows (e.g.
   * "Accept", "Allow all", "Got it"). Falls back to a built-in list.
   */
  acceptTexts: z.array(z.string()).optional(),
  /** If true, skip silently when no overlay is found. */
  optional: z.boolean().default(true),
  timeoutMs: z.number().int().positive().default(3000),
});
export type DismissOverlayFields = z.infer<typeof DismissOverlayFieldsSchema>;

/**
 * `scroll-until` — keep scrolling the page (or a container) until a
 * target selector or text appears in the DOM, or until the page
 * stops growing. One-step replacement for the `loop-while` +
 * `scroll` + `assert_visible` triple.
 */
export const ScrollUntilFieldsSchema = z.object({
  /** What to scroll inside; default = window. */
  containerTarget: z.string().optional(),
  /** Stop when this selector becomes visible OR text appears. */
  until: z.union([
    z.object({ targetVisible: z.string() }),
    z.object({ textVisible: z.string() }),
    z.object({ noMoreItems: z.literal(true) }),
  ]),
  direction: z.enum(['down', 'up']).default('down'),
  stepPx: z.number().int().positive().default(800),
  /** Hard cap on the number of scroll iterations. */
  maxScrolls: z.number().int().positive().default(40),
  /** Pause after each scroll to let lazy-loaded content render. */
  pauseMs: z.number().int().nonnegative().default(250),
});
export type ScrollUntilFields = z.infer<typeof ScrollUntilFieldsSchema>;

/**
 * `filter-out-sponsored` — flag sponsored / ad cards in a result
 * list so subsequent `with-row` and `assert-cell` steps can skip
 * them. Adds a `data-rynora-sponsored="true"` attribute to every
 * matched element.
 */
export const FilterOutSponsoredFieldsSchema = z.object({
  containerTarget: z.string().optional(),
  /**
   * CSS selectors / aria-label substrings / text patterns that mark
   * sponsored cards. Defaults cover Amazon, Google, LinkedIn.
   */
  sponsoredHints: z.array(z.string()).optional(),
  outputVar: z.string().optional(),
});
export type FilterOutSponsoredFields = z.infer<typeof FilterOutSponsoredFieldsSchema>;

/**
 * `solve-captcha` — handle a captcha challenge. Today we ship two
 * adapters: `mock-text` (the test fixture's "type the answer"
 * mode) and `pause-for-human` (operator manually solves and clicks
 * Continue). Extending to 2captcha/anticaptcha is one adapter file.
 */
export const SolveCaptchaAdapterSchema = z.enum(['mock-text', 'pause-for-human', '2captcha', 'anticaptcha']);
export const SolveCaptchaFieldsSchema = z.object({
  adapter: SolveCaptchaAdapterSchema,
  challengeTarget: z.string().optional(),
  /** Used for `mock-text` adapter — the literal answer to type. */
  mockAnswer: z.string().optional(),
  /** Vault key for the captcha-service API key. */
  secretRef: z.string().optional(),
  /** Where to type the answer; default detects the prompt input. */
  inputTarget: z.string().optional(),
  submitTarget: z.string().optional(),
  timeoutMs: z.number().int().positive().default(120_000),
});
export type SolveCaptchaFields = z.infer<typeof SolveCaptchaFieldsSchema>;

// ============================================================
// Helpers — JSON round-trip into the flat string field bag
// ============================================================

export function encodeStructured<T>(value: T): string {
  return JSON.stringify(value ?? null);
}

/**
 * Product Lock — structured JSON honesty:
 * - omitted / null / "" → `fallback` (existing omit semantics)
 * - valid JSON → parsed value
 * - nonblank malformed JSON (incl. whitespace-only) → **FAIL CLOSED** (never soft-empty)
 */
export class StructuredJsonError extends Error {
  constructor(message = 'structured field must be valid JSON') {
    super(message);
    this.name = 'StructuredJsonError';
  }
}

export function decodeStructured<T>(raw: string | undefined | null, fallback: T): T {
  // Omission only: null / undefined / "". Whitespace is nonblank → must parse or fail.
  if (raw == null || raw === '') return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    throw new StructuredJsonError('structured field must be valid JSON');
  }
}

/**
 * Parse a single string-encoded `Expectation` cell value, accepting
 * both the rich JSON form and the friendly shorthand sigils.
 */
export function parseExpectation(raw: string): Expectation {
  const trimmed = raw.trim();
  if (!trimmed) return { absolute: '' };
  if (trimmed.startsWith('JSON(') && trimmed.endsWith(')')) {
    const inner = trimmed.slice(5, -1);
    const parsed = decodeStructured<Expectation | null>(inner, null);
    if (parsed) return parsed;
  }
  if (trimmed.startsWith('+') || trimmed.startsWith('-')) {
    const n = Number(trimmed);
    if (!Number.isNaN(n)) return { delta: n };
  }
  if (trimmed.startsWith('=')) return { absolute: trimmed.slice(1) };
  if (trimmed.startsWith('>')) return { greaterThan: trimmed.slice(1) };
  if (trimmed.startsWith('<')) return { lessThan: trimmed.slice(1) };
  if (trimmed.startsWith('~')) {
    const body = trimmed.slice(1);
    const tolMatch = body.match(/^(.*?)\(\u00B1([\d.]+)\)$/);
    if (tolMatch) {
      return { formula: tolMatch[1].trim(), tolerance: Number(tolMatch[2]) };
    }
    return { formula: body.trim() };
  }
  if (trimmed.startsWith('/') && trimmed.endsWith('/')) {
    return { matches: trimmed.slice(1, -1) };
  }
  const n = Number(trimmed);
  if (!Number.isNaN(n)) return { delta: n };
  return { absolute: trimmed };
}
